import type { SortDirection } from "@opensea/graphql"
import { compareAsc } from "date-fns"

export type Value = string | number | Date | undefined

type ComparisonResult = {
  compare: number
  isDuplicate: boolean
}

export type PaginatorOptions<T> = {
  maxItems?: number
  insertIntoLastPlace?: boolean
  skipReorder?: boolean
  applyIf?: (current: T, update: T) => boolean
}

export const DEFAULT_PAGINATOR_OPTIONS = {
  maxItems: 300,
  insertIntoLastPlace: true,
  skipReorder: false,
} as const satisfies PaginatorOptions<unknown>

function compareValue(
  v1: Value,
  v2: Value,
  direction: SortDirection | undefined,
): number {
  let a: Value
  let b: Value

  if (direction === "ASC") {
    a = v1
    b = v2
  } else {
    a = v2
    b = v1
  }

  if (typeof a === "number" && typeof b === "number") {
    return a - b
  }
  if (typeof a === "string" && typeof b === "string") {
    return a.localeCompare(b)
  }
  if (a instanceof Date && b instanceof Date) {
    return compareAsc(a, b)
  }
  if (a === undefined && b === undefined) {
    return -1
  }
  if (a === undefined) {
    return direction === "ASC" ? 1 : -1
  }
  if (b === undefined) {
    return direction === "ASC" ? -1 : 1
  }
  throw new Error(`Unsupported value type a=${a.toString()} b=${b.toString()}`)
}

function compareValues(
  v1: Value[],
  v2: Value[],
  direction: SortDirection | undefined,
): ComparisonResult {
  if (v1.length !== v2.length) {
    throw new Error("Missmatch array length")
  }

  let compare = 0
  let isDuplicate = false

  for (let i = 0; i < v1.length; i++) {
    const a = v1[i]
    const b = v2[i]
    compare = compareValue(a, b, direction)
    isDuplicate = false

    if (compare !== 0) {
      break
    }

    // We assume last element in the values list is allways a unique id
    if (i === v1.length - 1) {
      isDuplicate = true
    }
  }

  return { compare, isDuplicate }
}

function findItem<T>(
  itemValues: Value[],
  items: T[],
  getValues: (item: T) => Value[],
  sortDirection: SortDirection | undefined,
): number {
  const itemIdValue = itemValues.at(-1)
  for (let i = 0; i < items.length; i++) {
    const elementIdValue = getValues(items[i])[itemValues.length - 1]
    const compare = compareValue(itemIdValue, elementIdValue, sortDirection)
    if (compare === 0) {
      return i
    }
  }
  return -1
}

function insertIntoSafeOrder<T>(
  item: T,
  itemValues: Value[],
  items: T[],
  getValues: (item: T) => Value[],
  sortDirection: SortDirection | undefined,
  options: PaginatorOptions<T> = DEFAULT_PAGINATOR_OPTIONS,
) {
  let low = 0
  let high = items.length - 1
  while (low <= high) {
    const mid = Math.floor((low + high) / 2)
    const { compare, isDuplicate } = compareValues(
      getValues(items[mid]),
      itemValues,
      sortDirection,
    )

    if (isDuplicate) {
      if (options.applyIf?.(items[mid], item) === false) {
        return items
      }
      items[mid] = item
      return items
    }

    if (compare < 0) {
      low = mid + 1
    } else if (compare > 0) {
      high = mid - 1
    } else {
      low = mid + 1
      break
    }
  }

  if (!shouldInsert(low, items, options)) {
    return items
  }

  items.splice(low, 0, item)
  if (!options.maxItems || items.length <= options.maxItems) {
    return items
  }
  return items.slice(0, options.maxItems)
}

export function remove<T>(
  item: T,
  items: T[],
  getValues: (item: T) => Value[],
  sortDirection: SortDirection | undefined,
  applyIf?: (current: T, update: T) => boolean,
) {
  const index = findItem(getValues(item), items, getValues, sortDirection)
  if (index === -1) {
    return items
  }

  if (applyIf?.(items[index], item) === false) {
    return items
  }

  const updatedItems = [...items]
  updatedItems.splice(index, 1)
  return updatedItems
}

export function updateInOrder<T>(
  items: T[],
  getValues: (item: T) => Value[],
  sortDirection: SortDirection | undefined,
  matchItem: (t: T) => boolean,
  updateFunction: (t: T) => T,
  options: PaginatorOptions<T> = DEFAULT_PAGINATOR_OPTIONS,
) {
  const index = items.findIndex(c => matchItem(c))
  if (index === -1) {
    return items
  }
  const updatedItems = [...items]
  // we are spreading here to create a new obj identity, which will force a re-render in list views.
  // if the caller would forget to do this, UI would not get updated
  const item = { ...updateFunction(updatedItems[index]) }
  if (options.applyIf?.(items[index], item) === false) {
    return items
  }
  if (options.skipReorder) {
    return updatedItems
  }
  const itemValues = getValues(item)
  updatedItems.splice(index, 1)

  return insertIntoSafeOrder(
    item,
    itemValues,
    updatedItems,
    getValues,
    sortDirection,
    { ...options, insertIntoLastPlace: true },
  )
}

export function insertInOrder<T>(
  item: T,
  items: T[],
  getValues: (item: T) => Value[],
  sortDirection: SortDirection | undefined,
  options?: PaginatorOptions<T>,
) {
  const updatedItems = [...items]
  const itemValues = getValues(item)
  // If we are sorting by single field (id), we know its safe to just insert in order as the list can't re-order
  if (itemValues.length <= 1) {
    return insertIntoSafeOrder(
      item,
      itemValues,
      updatedItems,
      getValues,
      sortDirection,
      options,
    )
  }

  // If we are sorting on multiple fields, we need to check if this is a update to an existing item
  const index = findItem(itemValues, updatedItems, getValues, sortDirection)

  // If its not a update/duplicate, we can just safely insert in order
  if (index === -1) {
    return insertIntoSafeOrder(
      item,
      itemValues,
      updatedItems,
      getValues,
      sortDirection,
      options,
    )
  }

  // We do this check in insertIntoSafeOrder as well, but since we are splicing before calling that
  // we need to do it here as well
  if (!shouldInsert(index, items, options)) {
    return items
  }

  // If its a update/duplicate, we remove it from the list, and re-insert the new item into appropriate position
  updatedItems.splice(index, 1)
  return insertIntoSafeOrder(
    item,
    itemValues,
    updatedItems,
    getValues,
    sortDirection,
    options,
  )
}

function shouldInsert<T>(
  index: number,
  items: T[],
  options?: PaginatorOptions<T>,
): boolean {
  return (
    options?.insertIntoLastPlace || index !== items.length || items.length === 0
  )
}
